import {
  DeleteInactiveUserMutation,
  DeleteInactiveUserMutationVariables,
  ResendInvitationMutation,
  ResendInvitationMutationVariables,
  RevokeInvitationMutation,
  RevokeInvitationMutationVariables,
} from "@/__generated__/graphql";
import {
  DELETE_INACTIVE_USER_MUTATION,
  RESEND_INVITATION_MUTATION,
  REVOKE_INVITATION_MUTATION,
} from "@/components/SettingsSidepanel/settings.graphql.ts";
import { PendingInvitationProps } from "@/components/SettingsSidepanel/settings.types.ts";
import SettingsSheetHeader from "@/components/SettingsSidepanel/SettingsSheetHeader.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useUserContext } from "@/contexts/UserContextProvider.tsx";
import { USERS_QUERY } from "@/graphql/queries/common/UsersQuery.ts";
import {
  INVITE_TOKEN_TIMEOUT_SECONDS,
  userRoles,
} from "@/helpers/constants.ts";
import { useMutation } from "@apollo/client";
import { captureException } from "@sentry/react";
import { Info, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const PendingInvitations: React.FC<PendingInvitationProps> = ({
  invitations,
  onBack,
  onRequestClose,
}) => {
  const { currentUser } = useUserContext();
  const [pendingUsers, setPendingUsers] = useState(invitations);

  const [removeInvitationMutation] = useMutation<
    DeleteInactiveUserMutation,
    DeleteInactiveUserMutationVariables
  >(DELETE_INACTIVE_USER_MUTATION, {
    refetchQueries: [{ query: USERS_QUERY }],
  });

  const [resendInvitationMutation] = useMutation<
    ResendInvitationMutation,
    ResendInvitationMutationVariables
  >(RESEND_INVITATION_MUTATION, {
    refetchQueries: [{ query: USERS_QUERY }],
  });

  const [revokeInvitationMutation] = useMutation<
    RevokeInvitationMutation,
    RevokeInvitationMutationVariables
  >(REVOKE_INVITATION_MUTATION, {
    refetchQueries: [{ query: USERS_QUERY }],
  });

  if (!currentUser) return null;

  return (
    <div className="flex flex-col h-full">
      <SettingsSheetHeader
        title="Pending Invitations"
        onRequestClose={onRequestClose}
        onBack={onBack}
      />{" "}
      <div className="flex flex-col gap-4 p-4 rounded-2xl border flex-1 border-accent-foreground overflow-auto">
        <div className="sticky top-0 z-10 flex items-center gap-2 rounded-2xl bg-[#FFAB4D33] p-4">
          <div className="rounded-full bg-yellow-600 p-2">
            <Info className="size-5 text-white" />
          </div>
          <p className="text-service-3d-tracking text-sm">
            Invitations expire after 3 days. You can resend an invitation after
            it expires.
          </p>
        </div>
        <div className="flex-1 overflow-auto">
          <div className="flex flex-col gap-4">
            {pendingUsers.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                No pending invitations.
              </div>
            ) : (
              pendingUsers.map((user) => {
                const isExpired =
                  typeof user.invitationExpiresInSeconds === "number" &&
                  user.invitationExpiresInSeconds < 0;

                return (
                  <div
                    key={user.id}
                    className="flex flex-wrap items-start gap-4 rounded-lg bg-button p-4 shadow-sm"
                  >
                    <Button
                      disabled={!isExpired && !user.invitationRevokedBy}
                      size="icon"
                      title="Remove Invitation"
                      onClick={async () => {
                        try {
                          const result = await removeInvitationMutation({
                            variables: { userId: user.id },
                          });
                          if (result.data?.deleteInactiveUser?.success) {
                            setPendingUsers((prev) =>
                              prev.filter((u) => u.id !== user.id),
                            );
                          } else {
                            toast.error(
                              result.data?.deleteInactiveUser?.message ??
                                "Failed to remove invitation.",
                            );
                          }
                        } catch (err) {
                          captureException(err);
                          toast.error("Error removing invitation.");
                        }
                      }}
                    >
                      <X />
                    </Button>

                    <div className="min-w-0 flex-1">
                      <div className="text-base font-normal text-secondary-foreground flex items-center gap-2">
                        <p>{user.email}</p>
                        <span className="rounded bg-border p-1 text-sm">
                          {userRoles[user.role]}
                        </span>
                      </div>
                      <p className="text-xs">
                        {user.invitationRevokedBy ? (
                          <>
                            <span className="font-semibold text-destructive">
                              REVOKED
                            </span>{" "}
                            by {user.invitationRevokedBy.firstName}{" "}
                            {user.invitationRevokedBy.lastName}
                          </>
                        ) : isExpired ? (
                          <span className="font-semibold text-destructive">
                            EXPIRED
                          </span>
                        ) : (
                          <>
                            <span className="font-semibold text-green-500">
                              INVITED
                            </span>{" "}
                            by {user.inviter?.firstName}{" "}
                            {user.inviter?.lastName}
                            {typeof user.invitationExpiresInSeconds ===
                              "number" && (
                              <>
                                &nbsp;(Expires in&nbsp;
                                {(
                                  user.invitationExpiresInSeconds /
                                  (INVITE_TOKEN_TIMEOUT_SECONDS / 72)
                                ).toFixed(0)}
                                &nbsp;hours)
                              </>
                            )}
                          </>
                        )}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {user.invitationRevokedBy ? (
                        <Button
                          disabled
                          className="border border-red-500 text-red-500"
                        >
                          Revoked
                        </Button>
                      ) : (
                        <>
                          <Button
                            className="bg-link text-black hover:bg-link/50 dark:text-white"
                            onClick={async () => {
                              try {
                                const result = await resendInvitationMutation({
                                  variables: { userId: user.id },
                                });
                                if (result.data?.resendInvitation?.success) {
                                  setPendingUsers((prev) =>
                                    prev.map((u) =>
                                      u.id === user.id
                                        ? {
                                            ...u,
                                            invitationExpiresInSeconds:
                                              INVITE_TOKEN_TIMEOUT_SECONDS,
                                          }
                                        : u,
                                    ),
                                  );
                                } else {
                                  toast.error(
                                    result.data?.resendInvitation?.message ??
                                      "Failed to resend invitation.",
                                  );
                                }
                              } catch (err) {
                                captureException(err);
                                toast.error("Error resending invitation.");
                              }
                            }}
                          >
                            Re-send
                          </Button>

                          {!isExpired && (
                            <Button
                              className="bg-red-500 text-black hover:bg-red-500/50 dark:text-white"
                              onClick={async () => {
                                try {
                                  const result = await revokeInvitationMutation(
                                    {
                                      variables: { userId: user.id },
                                    },
                                  );
                                  if (result.data?.revokeInvitation?.success) {
                                    setPendingUsers((prev) =>
                                      prev.map((u) =>
                                        u.id === user.id
                                          ? {
                                              ...u,
                                              invitationRevokedBy: {
                                                __typename: "User",
                                                id: currentUser.id,
                                                firstName:
                                                  currentUser.firstName,
                                                lastName: currentUser.lastName,
                                              },
                                            }
                                          : u,
                                      ),
                                    );
                                  } else {
                                    toast.error(
                                      result.data?.revokeInvitation?.message ??
                                        "Failed to revoke invitation.",
                                    );
                                  }
                                } catch (err) {
                                  captureException(err);
                                  toast.error("Error revoking invitation.");
                                }
                              }}
                            >
                              Revoke
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingInvitations;
